import {
  ShieldCheck,
  Gauge,
  Sparkles,
  Network,
  Workflow,
  Plug,
  Webhook,
  Bot,
  BadgeCheck,
  Settings,
  LayoutGrid,
  FolderGit2,
  Inbox,
  Activity,
  Waypoints,
} from "lucide-react";
import type { AppDef, AppId } from "@/types";
import { UTILITIES } from "@/data/utilities";

export const APPS: Record<AppId, AppDef> = {
  ares: {
    id: "ares",
    name: "ARES",
    tagline: "Verification-first development",
    icon: ShieldCheck,
    gradient: "from-amber-400 to-orange-600",
    accent: "#f59e0b",
    defaultSize: { w: 980, h: 660 },
    group: "product",
  },
  athena: {
    id: "athena",
    name: "ATHENA",
    tagline: "API readiness platform",
    icon: Gauge,
    gradient: "from-sky-400 to-blue-600",
    accent: "#3b82f6",
    defaultSize: { w: 960, h: 640 },
    group: "product",
  },
  garlic: {
    id: "garlic",
    name: "GARLIC",
    tagline: "Agent specialization",
    icon: Sparkles,
    gradient: "from-violet-400 to-fuchsia-600",
    accent: "#a855f7",
    defaultSize: { w: 960, h: 640 },
    group: "product",
  },
  byonc: {
    id: "byonc",
    name: "BYONC",
    tagline: "Hybrid inference routing",
    icon: Network,
    gradient: "from-emerald-400 to-teal-600",
    accent: "#10b981",
    defaultSize: { w: 1040, h: 700 },
    group: "product",
  },
  appgraph: {
    id: "appgraph",
    name: "AppGraph",
    tagline: "Visualize app dependencies",
    icon: Waypoints,
    gradient: "from-rose-400 to-pink-600",
    accent: "#f43f5e",
    defaultSize: { w: 1040, h: 700 },
    group: "product",
  },
  workspaces: {
    id: "workspaces",
    name: "Workspaces",
    tagline: "Cloud dev environments",
    icon: FolderGit2,
    gradient: "from-teal-400 to-emerald-600",
    accent: "#14b8a6",
    defaultSize: { w: 1200, h: 780 },
    group: "system",
  },
  tasks: {
    id: "tasks",
    name: "Tasks",
    tagline: "Task command center",
    icon: Inbox,
    gradient: "from-orange-400 to-rose-600",
    accent: "#f97316",
    defaultSize: { w: 1200, h: 780 },
    group: "system",
  },
  workflows: {
    id: "workflows",
    name: "Workflow Builder",
    tagline: "Hook → Process → Publish",
    icon: Workflow,
    gradient: "from-rose-400 to-pink-600",
    accent: "#f43f5e",
    defaultSize: { w: 1320, h: 820 },
    group: "system",
  },
  execution: {
    id: "execution",
    name: "Execution Center",
    tagline: "Live operational heartbeat",
    icon: Activity,
    gradient: "from-emerald-400 to-cyan-600",
    accent: "#22d3ee",
    defaultSize: { w: 1280, h: 820 },
    group: "system",
  },
  integrations: {
    id: "integrations",
    name: "Integrations",
    tagline: "Integration center",
    icon: Plug,
    gradient: "from-cyan-400 to-sky-600",
    accent: "#06b6d4",
    defaultSize: { w: 1180, h: 780 },
    group: "system",
  },
  hooks: {
    id: "hooks",
    name: "Hooks",
    tagline: "Event trigger chains",
    icon: Webhook,
    gradient: "from-indigo-400 to-purple-600",
    accent: "#6366f1",
    defaultSize: { w: 960, h: 640 },
    group: "system",
  },
  agents: {
    id: "agents",
    name: "Agents",
    tagline: "Autonomous fleet",
    icon: Bot,
    gradient: "from-amber-300 to-amber-600",
    accent: "#f59e0b",
    defaultSize: { w: 900, h: 620 },
    group: "system",
  },
  verification: {
    id: "verification",
    name: "Verification",
    tagline: "Proof & evidence",
    icon: BadgeCheck,
    gradient: "from-green-400 to-emerald-600",
    accent: "#22c55e",
    defaultSize: { w: 900, h: 620 },
    group: "system",
  },
  settings: {
    id: "settings",
    name: "Settings",
    tagline: "System preferences",
    icon: Settings,
    gradient: "from-slate-400 to-slate-600",
    accent: "#94a3b8",
    defaultSize: { w: 820, h: 580 },
    group: "system",
  },
  launcher: {
    id: "launcher",
    name: "All Apps",
    tagline: `${UTILITIES.length} internal apps`,
    icon: LayoutGrid,
    gradient: "from-amber-400 to-yellow-600",
    accent: "#eab308",
    defaultSize: { w: 1000, h: 660 },
    group: "system",
  },
};

/** Order of icons in the dock */
export const DOCK_ORDER: AppId[] = [
  "ares",
  "athena",
  "garlic",
  "byonc",
  "appgraph",
  "workspaces",
  "tasks",
  "workflows",
  "execution",
  "integrations",
  "hooks",
  "agents",
  "verification",
  "settings",
];

export const APP_LIST = Object.values(APPS);
